import { User } from "features/user/types";
import { isUserPlan } from "features/upsell/plan-utils";
import { hasEntitlement, getEntitlementValue } from "utils/has-entitlement";
import { entitlementsPermission } from "utils/entitlements-permission";
import { isReverseTrial } from "utils/reverse-trial";

/**
 * Consolidated feature gate helpers.
 *
 * Each `hasX` helper answers "does this user have access to feature X?" and
 * uses the same resolution order everywhere:
 *
 *  1. Reverse trial → true (every feature unlocked during the trial)
 *  2. Entitlement check on user.preferences.entitlements
 *  3. Legacy plan-code fallback for users whose backend hasn't migrated to
 *     entitlements yet (paid users on older plans)
 *
 * The plan-code fallback mirrors the previous hard-coded rules so paid
 * customers continue to see the correct gating until their account's
 * entitlements are populated.
 *
 * `needsX` helpers are the inverse — "does this user need to upgrade to get
 * feature X?" — which is the shape many existing call sites expect.
 */

/**
 * Guard against unhydrated user payloads. The legacy plan-code fallback
 * uses `!isUserPlan(user, "sked-fundamentals", ...)` which silently grants
 * access when `user.plan` is undefined (because `isUserPlan` returns false
 * and `!false === true`). Several call sites pass partially-hydrated users
 * (e.g., the `/profile` GET payload) so we must refuse to use the legacy
 * fallback unless we have a real plan code to evaluate.
 */
const canUseLegacyPlanFallback = (user: User): boolean =>
  typeof user.plan === "string" && user.plan.length > 0;

// ---------- Teamwork / collaboration ----------

export const hasTeamwork = (user: User | null | undefined): boolean => {
  if (!user) return false;
  if (isReverseTrial(user)) return true;
  if (hasEntitlement(user, "teamwork")) return true;
  if (!canUseLegacyPlanFallback(user)) return false;
  // Legacy: anything above fundamentals had teamwork.
  return !isUserPlan(user, "sked-fundamentals");
};

export const needsTeamworkUpsell = (user: User | null | undefined): boolean =>
  !hasTeamwork(user);

// ---------- Content labels (planning + reporting) ----------

/**
 * Chargebee defines exactly TWO label entitlements, and these are the only
 * keys that exist on the catalog:
 *  - `labels-create`   — content-label planning / creation.
 *  - `labels-analytics` — content-label reporting / analytics.
 *
 * SKE-3777-followup: every label gate previously checked `labels` /
 * `labels-planning` / `labels-reporting`, which were NEVER created in
 * Chargebee. The entitlement check therefore always missed and the gate fell
 * through to the plan-code fallback — so a plan whose `labels-create=true`
 * (e.g. Basic/Grow after SKE-3777) still showed the feature as locked. These
 * helpers now read the real keys, so the entitlement is authoritative; the
 * plan-code branch only fires for legacy/unmigrated paid users (those with no
 * label entitlement at all), guarded by `canUseLegacyPlanFallback`.
 */
export const hasLabelsPlanning = (user: User | null | undefined): boolean => {
  if (!user) return false;
  if (isReverseTrial(user)) return true;
  if (hasEntitlement(user, "labels-create")) return true;
  if (!canUseLegacyPlanFallback(user)) return false;
  // Legacy/unmigrated fallback: anything above Basic/Grow had label planning.
  return !isUserPlan(user, "sked-fundamentals", "sked-essentials");
};

export const needsLabelsPlanningUpsell = (
  user: User | null | undefined
): boolean => !hasLabelsPlanning(user);

export const hasLabelsReporting = (user: User | null | undefined): boolean => {
  if (!user) return false;
  if (isReverseTrial(user)) return true;
  if (hasEntitlement(user, "labels-analytics")) return true;
  if (!canUseLegacyPlanFallback(user)) return false;
  // Legacy/unmigrated fallback: enterprise-only in the pre-2024 rules.
  return !isUserPlan(
    user,
    "sked-fundamentals",
    "sked-essentials",
    "sked-professional"
  );
};

export const needsLabelsReportingUpsell = (
  user: User | null | undefined
): boolean => !hasLabelsReporting(user);

// ---------- Product tagging (Instagram product tags) ----------

/**
 * SKE-3927: the server already reads a `product-tags` entitlement
 * (featureAccess.hasProductTags) but the Chargebee catalog key is being added
 * in the same change. Until the catalog is applied, the guarded plan-code
 * fallback preserves today's behavior: product tagging is unavailable on Basic
 * (fundamentals) and legacy freelancer plans, available on Grow and above.
 */
export const hasProductTags = (user: User | null | undefined): boolean => {
  if (!user) return false;
  if (isReverseTrial(user)) return true;
  if (hasEntitlement(user, "product-tags")) return true;
  if (!canUseLegacyPlanFallback(user)) return false;
  // Legacy fallback mirrors the historical block set (Basic + legacy
  // freelancer). "sked-freelancer" isn't in SkedPlanCodeName, so match the raw
  // plan string rather than isUserPlan. canUseLegacyPlanFallback guarantees a
  // non-empty plan here.
  const plan = user.plan ?? "";
  return (
    !plan.includes("sked-fundamentals") && !plan.includes("sked-freelancer")
  );
};

// ---------- UGC media-library search pickers (hashtag / creator / tagged-in) ----------

/**
 * SKE-3927: gates the UGC search pickers behind the new `ugc-library`
 * entitlement. Legacy fallback preserves today's behavior — blocked on Basic
 * (fundamentals) only, available on Grow and above.
 */
export const hasUgcLibrary = (user: User | null | undefined): boolean => {
  if (!user) return false;
  if (isReverseTrial(user)) return true;
  if (hasEntitlement(user, "ugc-library")) return true;
  if (!canUseLegacyPlanFallback(user)) return false;
  return !isUserPlan(user, "sked-fundamentals");
};

// ---------- UTM customization ----------

/**
 * SKE-3927: gates per-account UTM customization behind the new
 * `utm-customization` entitlement. Legacy fallback preserves today's behavior —
 * blocked on Basic (fundamentals) only, available on Grow and above.
 */
export const hasUtmCustomization = (user: User | null | undefined): boolean => {
  if (!user) return false;
  if (isReverseTrial(user)) return true;
  if (hasEntitlement(user, "utm-customization")) return true;
  if (!canUseLegacyPlanFallback(user)) return false;
  return !isUserPlan(user, "sked-fundamentals");
};

// ---------- Inbox tagging ----------

/**
 * SKE-2861: gate every inbox-tag UI surface behind the `inbox-tagging`
 * Chargebee switch entitlement.
 *
 * Unlike most other helpers in this file, this one has NO reverse-trial
 * bypass and NO legacy plan-code fallback. Per the rollout decision —
 * "default to off for all plans" — every customer (including reverse-
 * trial users) defaults off, and the only way to grant access is via a
 * Chargebee plan-item entitlement or a per-customer subscription
 * override. Existing tag users will lose access on deploy until their
 * plan/subscription is granted the entitlement.
 */
export const hasInboxTagging = (user: User | null | undefined): boolean =>
  hasEntitlement(user, "inbox-tagging");

// ---------- Frontify media integration ----------

export const hasFrontify = (user: User | null | undefined): boolean => {
  if (!user) return false;
  if (isReverseTrial(user)) return true;
  if (hasEntitlement(user, "frontify")) return true;
  if (!canUseLegacyPlanFallback(user)) return false;
  return isUserPlan(user, "sked-enterprise");
};

// ---------- Approvals ----------

/**
 * Valid values for the `post-status-version` entitlement that grant approvals.
 * Mirrors chargebee-plan-entitlements.json: null (no access), basic (Grow —
 * 2 fixed non-editable statuses), advanced (Accelerate / Custom — full
 * editable). The "standard" tier is reserved/unused per the CB JSON.
 *
 * NOTE: the server gate (schedugram-server featureAccess.ts) DOES grant
 * "standard" because it has a full standard-tier implementation
 * (collaboration.ts standardStatuses template + permission model). We omit it
 * here on purpose (SKE-3599): CB never emits "standard", so the divergence is
 * not observable in practice. If CB ever starts emitting it, reconcile both
 * gates together.
 */
const POST_STATUS_GRANTED_VALUES = new Set(["basic", "advanced"]);
const POST_STATUS_ENTITLEMENT_KEY = "post-status-version";

const hasPostStatusEntitlementKey = (user: User): boolean =>
  Object.prototype.hasOwnProperty.call(
    user.preferences?.entitlements ?? {},
    POST_STATUS_ENTITLEMENT_KEY
  );

/**
 * Legacy escape hatch for sales-assisted fundamentals/essentials accounts
 * whose `postStatuses` array was pre-configured by Sales before the
 * `post-status-version` entitlement existed. Gated by `hasTeamwork` to
 * match the old `isCollaborationEnabled === true` requirement, so a user
 * who had statuses configured on a higher plan but later downgraded
 * doesn't regain access they shouldn't have.
 *
 * Callers (currently only `hasApprovals`) are expected to short-circuit on
 * `isReverseTrial` before invoking this helper; the reverse-trial grant
 * lives upstream rather than being repeated here.
 */
const hasSalesConfiguredApprovals = (user: User): boolean =>
  hasTeamwork(user) && !!user.postStatuses && user.postStatuses.length > 0;

/**
 * True if the user has access to approvals / post statuses.
 *
 * The entitlement check reads `post-status-version` and is authoritative when
 * the key is present: only `"basic"` and `"advanced"` grant access. Any other
 * explicit value (`"null"`, `"none"`, `""`, `"standard"`, unrecognised) means
 * no access — the sales/plan fallback is NOT reached in that case.
 *
 * Falls back to `hasSalesConfiguredApprovals` (and then the legacy plan-code
 * check) only when the `post-status-version` key is entirely absent from the
 * entitlements payload (e.g. cache miss / pre-entitlement account).
 */
export const hasApprovals = (user: User | null | undefined): boolean => {
  if (!user) return false;
  if (isReverseTrial(user)) return true;
  if (hasPostStatusEntitlementKey(user)) {
    const postStatusVersion = getEntitlementValue(
      user,
      POST_STATUS_ENTITLEMENT_KEY
    );
    return Boolean(
      postStatusVersion && POST_STATUS_GRANTED_VALUES.has(postStatusVersion)
    );
  }
  if (hasSalesConfiguredApprovals(user)) return true;
  if (!canUseLegacyPlanFallback(user)) return false;
  return !isUserPlan(user, "sked-fundamentals", "sked-essentials");
};

/**
 * True if the user has the External Approvals add-on (or a plan that includes
 * it). Driven by the `external-approvals` switch entitlement, NOT inferred
 * from the post-status tier.
 *
 * Internal Approvals add-on customers get full editable post statuses
 * (`post-status-version=advanced`) but DO NOT get external review unless they
 * also pay for External Approvals.
 *
 * DEPLOY NOTE: ship after the server backfill for `external-approvals` has
 * run for all existing paid customers who bundled external review in their
 * plan (Accelerate / Grow). Otherwise those users will temporarily lose
 * access until their entitlements are populated. See
 * `apps/schedugram-server/scripts/backfill-cache-entitlements.js`.
 */
export const hasExternalApprovals = (
  user: User | null | undefined
): boolean => {
  if (!user) return false;
  if (isReverseTrial(user)) return true;
  // SKE-3599: canAccessExternalReviewLinks now embeds the plan-tier fallback
  // (Accelerate / Enterprise / Custom) for entitlement-payload-missing
  // cases. The previous bespoke `isUserPlan(user, "sked-enterprise")` line
  // missed sked-professional (Accelerate post-rebrand) and sked-enterprise-
  // custom; it's now redundant and removed.
  return entitlementsPermission.canAccessExternalReviewLinks(user);
};

/**
 * True if the user is on the basic post-status tier (Grow plan baseline) and
 * needs to purchase the Internal Approvals add-on to customize statuses.
 *
 * This is the upsell trigger for the post-status setup form's "add custom
 * state" CTA — Grow customers get 2 read-only statuses by default.
 */
export const needsInternalApprovalsAddonUpsell = (
  user: User | null | undefined
): boolean => {
  if (!user) return false;
  if (isReverseTrial(user)) return false;
  return entitlementsPermission.getPostStatusVersion(user) === "basic";
};

/**
 * Inverse of `hasExternalApprovals` — true when the user needs to upgrade
 * (either via the External Approvals add-on or by upgrading to Accelerate).
 */
export const needsExternalApprovalsAddonUpsell = (
  user: User | null | undefined
): boolean => !hasExternalApprovals(user);

/** Default-off workspace admin switch; mirrors the server gate. */
export const hasApprovalWorkflows = (
  user:
    | {
        preferences?: {
          enableApprovalWorkflows?: unknown;
          entitlements?: Record<string, unknown>;
        };
      }
    | null
    | undefined
): boolean => user?.preferences?.enableApprovalWorkflows === true;
